import { useCallback, useEffect, useMemo, useState } from 'react';

import { Outlet, matchPath, useLocation } from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';
import { CopilotKitProvider } from '@copilotkit/react-core/v2';

import AgentAssistantPopup from '@/components/shared/AgentAssistantPopup';
import { useAgentChatSessions } from '@/hooks/useAgentChatSessions';
import { useAgentFeedback } from '@/hooks/useAgentFeedback';
import { ANALYTICS_PLACEHOLDER_ROUTES } from '@/pages/AnalyticsPage/constants';
import { useSession } from '@/providers/SessionProvider/SessionProvider';
import { ConnectableHttpAgent } from '@/utils/copilotKit/ConnectableHttpAgent';
import { ANALYTICS_AGENT_ID } from '@/utils/copilotKit/constants';

import { getWelcomeSuggestionsForPath, pickRandom } from './welcomeSuggestions';

const analyticsAgentBaseUrl = import.meta.env.VITE_INK_AGENTS_URI as
  | string
  | undefined;
const analyticsAgentUrl = `${analyticsAgentBaseUrl}/api/v1/${ANALYTICS_AGENT_ID}`;

const getAnalyticsPlaceholder = (pathname: string): string => {
  const matchedRoute = ANALYTICS_PLACEHOLDER_ROUTES.find(({ pattern }) =>
    matchPath(pattern, pathname),
  );

  return matchedRoute?.placeholder ?? 'Ask about your Analytics';
};

/**
 * Route layout element for the campaign/creative analytics pages. Mounts the
 * CopilotKitProvider ONCE above the analytics <Outlet/> so (a) the assistant
 * thread persists across index <-> detail navigation, and (b) any descendant
 * page/tab/header can call useAgentContext directly.
 *
 * The provider is ALWAYS rendered (useAgentContext throws without it). The
 * popup UI is gated by enableAiFeatures and the presence of a client ID.
 */
const AnalyticsAssistantLayout = (): JSX.Element => {
  const { getAccessTokenSilently } = useAuth0();
  const { currentClient } = useSession();
  const clientId = currentClient?.id;
  const { pathname } = useLocation();
  const placeholder = useMemo(
    () => getAnalyticsPlaceholder(pathname),
    [pathname],
  );

  const [threadId, setThreadId] = useState<string | undefined>(undefined);
  useEffect(() => {
    setThreadId(clientId ? crypto.randomUUID() : undefined);
  }, [clientId]);

  // Agent must be STABLE across thread switches (see original AnalyticsAssistant
  // notes): keyed only on the auth callback.
  const agent = useMemo(
    () =>
      new ConnectableHttpAgent({
        url: analyticsAgentUrl,
        getAuthToken: getAccessTokenSilently,
      }),
    [getAccessTokenSilently],
  );

  const headers = useMemo(
    () => (clientId ? { 'X-CLIENT-ID': clientId } : undefined),
    [clientId],
  );

  const selfManagedAgents = useMemo(
    () => ({ [ANALYTICS_AGENT_ID]: agent }),
    [agent],
  );

  const [historyOpened, setHistoryOpened] = useState(false);
  const { sessions, isLoading, error, refetch, renameSession, deleteSession } =
    useAgentChatSessions({
      agentId: ANALYTICS_AGENT_ID,
      enabled: historyOpened && !!clientId,
    });

  const handleOpenHistory = useCallback(() => {
    setHistoryOpened((alreadyOpened) => {
      if (alreadyOpened) refetch();
      return true;
    });
  }, [refetch]);

  // Random picks are re-rolled whenever the path changes or a new chat starts.
  const [welcomeSuggestions, setWelcomeSuggestions] = useState(() =>
    pickRandom(getWelcomeSuggestionsForPath(pathname)),
  );
  useEffect(() => {
    setWelcomeSuggestions(pickRandom(getWelcomeSuggestionsForPath(pathname)));
  }, [pathname]);

  const handleNewChat = useCallback(() => {
    setThreadId(crypto.randomUUID());
    setWelcomeSuggestions(pickRandom(getWelcomeSuggestionsForPath(pathname)));
  }, [pathname]);

  const handleDeleteThread = useCallback(
    async (id: string) => {
      await deleteSession(id);
      if (id === threadId) handleNewChat();
    },
    [deleteSession, threadId, handleNewChat],
  );

  const { submitFeedback } = useAgentFeedback({
    agentId: ANALYTICS_AGENT_ID,
  });

  const handleSubmitFeedback = useCallback(
    async ({ vote, comment }: { vote: 'up' | 'down'; comment?: string }) => {
      if (!threadId) {
        throw new Error('No active conversation');
      }
      await submitFeedback({ threadId, name: 'thumbs', value: vote, comment });
    },
    [threadId, submitFeedback],
  );

  const feedbackProp = useMemo(
    () => ({ onSubmit: handleSubmitFeedback }),
    [handleSubmitFeedback],
  );

  // TODO: feature_flag_cleanup: enableAiFeatures
  const popupEnabled = !!currentClient?.enableAiFeatures && !!clientId;

  return (
    <CopilotKitProvider selfManagedAgents={selfManagedAgents} headers={headers}>
      <Outlet />
      {popupEnabled && (
        <AgentAssistantPopup
          agentId={ANALYTICS_AGENT_ID}
          threadId={threadId}
          labels={{ modalHeaderTitle: 'Ask AI' }}
          placeholder={placeholder}
          history={{
            sessions,
            isLoading,
            error,
            currentThreadId: threadId,
            onSelectThread: setThreadId,
            onNewChat: handleNewChat,
            onOpen: handleOpenHistory,
            onRenameThread: renameSession,
            onDeleteThread: handleDeleteThread,
          }}
          welcome={{
            heading: 'Analyse your performance',
            subtitle:
              'Start with a suggestion or ask a question about your data',
            suggestions: welcomeSuggestions,
          }}
          feedback={feedbackProp}
          showBetaTag={true}
        />
      )}
    </CopilotKitProvider>
  );
};

export default AnalyticsAssistantLayout;
